import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  ApiSetActiveThemeResponse,
  ApiUnlockThemeResponse,
  ApiUserThemesResponse,
} from '@/lib/api/types'

export function useUserThemeApi(userId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient()

  const {
    data: themeData,
    isLoading: loading,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ['user-themes', userId],
    queryFn: async () => {
      return api.get<ApiUserThemesResponse>('/themes/me')
    },
    enabled: Boolean(userId) && enabled,
    staleTime: 60000,
  })

  const activeThemeId = themeData?.activeThemeId ?? null
  const unlockedThemeIds = themeData?.unlockedThemeIds ?? []

  const setThemeMutation = useMutation({
    mutationFn: async (themeId: string) => {
      return api.put<ApiSetActiveThemeResponse>('/themes/active', { themeId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-themes', userId] })
    },
  })

  const setTheme = useCallback(
    async (themeId: string) => {
      return setThemeMutation.mutateAsync(themeId)
    },
    [setThemeMutation]
  )

  const getThemeCSS = useCallback(async (themeId: string): Promise<string> => {
    const response = await api.get<{ css: string }>(`/themes/${themeId}/css`)
    return response.css
  }, [])

  const unlockThemeMutation = useMutation({
    mutationFn: async (payload: { themeId: string; source: string }) => {
      return api.post<ApiUnlockThemeResponse>('/themes/unlock', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-themes', userId] })
    },
  })

  const unlockTheme = useCallback(
    async (themeId: string, source: string = 'purchase') => {
      await unlockThemeMutation.mutateAsync({ themeId, source })
    },
    [unlockThemeMutation]
  )

  return {
    activeThemeId,
    unlockedThemeIds,
    themeData: themeData ?? null,
    loading,
    isFetched,
    isSettingTheme: setThemeMutation.isPending,
    setTheme,
    getThemeCSS,
    unlockTheme,
    refetch,
  }
}
